import { useState } from 'react';
import { useParams, useNavigate, type NavigateFunction } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

/**
 * Shared setup for the LMS edit/create form pages (LmsContentEditorPage,
 * LmsSessionFormPage), which both opened with the identical six lines:
 *
 *   const { id } = useParams<{ id: string }>();
 *   const navigate = useNavigate();
 *   const isEditing = Boolean(id);
 *   const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
 *   const [loading, setLoading] = useState(isEditing);
 *   const [saving, setSaving] = useState(false);
 *
 * Extracted verbatim. The internal hook-call order (useParams, useNavigate,
 * useAuthStore, useState, useState) is preserved, and each page still calls
 * this as its FIRST hook — so the overall hook ordering is unchanged.
 */
interface EditFormPageSetup {
  id: string | undefined;
  navigate: NavigateFunction;
  isEditing: boolean;
  projectId: number | undefined;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  saving: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useEditFormPageSetup(): EditFormPageSetup {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  return { id, navigate, isEditing, projectId, loading, setLoading, saving, setSaving };
}
